import { Icon } from "armstrong-react";
import React from "react";
import { connect } from "react-redux";
import Sticky from "react-stickynode";
import * as zenscroll from "zenscroll";
import { LazyLoadImage, LinkButton, StarRating } from "../../../../core/atoms/atoms";
import { CardContainer } from "../../../../shared/components/inspiration/cardContainer";
import { CollectionKeyHelper } from "../../../../shared/helpers/contentCollectionHelpers";
import { MatchState } from "../../../../shared/helpers/reduxHelpers";
import { ResponsiveBreakpoints } from "../../../../../../packages/ui/src/utils/uiHelpers";
import { useWindowDimensions } from "../../../../core/atoms/atoms";
import { LocationContentSection } from "../../../components/location/locationContentSection";
import { NavMenuItem } from "../../../components/navMenu/navMenu";
import { PageScrollNav } from "../../../components/navMenu/pageScrollNav";
import { SwipeCarousel } from "../../../components/swipeCarousel/swipeCarousel";
import { TrustSection } from "../../../components/trustSection/trustSection";
import CountrySidebarCta from "./countrySidebarCta";
import * as WebRoutes from "@travellocal/utils/webRoutes";

import "./overview.scss";

interface StoreProps {
  countryState: IState<ICountry>;
  itinerariesContentState: IState<IContentCollection<IContentSampleItinerary>>;
  sectionContentState: IState<IContentCollection<IContentLocationSection>>;
}

const getDomItemYPositionWithStickyNavOffset = (id: string) => {
  const domItem = document.querySelector(`[data-scroll-id="${id}"]`);

  if (!domItem) {
    return;
  }

  const elementYOffset = domItem.getBoundingClientRect().top - document.body.getBoundingClientRect().top;

  // return y offset including the height of the secondary nav, to force the scroll to sit just under the nav
  return elementYOffset - 165;
};

const getNavItems = (sectionContent: IContentLocationSection[]): NavMenuItem[] => {
  const navItems: NavMenuItem[] = [];

  for (const content of sectionContent) {
    navItems.push({
      label: content.content.navTitle || content.title,
      id: `content-${content.id}`,
      slug: content.slug,
    });
  }

  return navItems;
};

const jumpToContentSections = () => {
  const element = document.getElementsByClassName("country-overview__sections")[0] as HTMLElement;
  zenscroll.to(element);
};

const PageComponent = (props: StoreProps) => {
  const { countryState, sectionContentState, itinerariesContentState } = props;

  return (
    <>
      {MatchState(countryState, {
        // Error & pending state handled in countryIndex.tsx
        data: country => {
          const { images = [] } = country;

          // #1531 Enable selection of lto images
          const ltoFallbackImageUrl = country.heroImage ? country.heroImage.smallUrl : "/images/missing-image.jpg";

          // Use the chosen image, or the primary LTO's team photo
          const chosenLtoMainImage = images.find(x => x.locationImageType === LocationImageType.LtoMain);
          const ltoMainImageUrl = chosenLtoMainImage != null
            ? chosenLtoMainImage.smallUrl
            : (country.defaultLto && country.defaultLto.image ? country.defaultLto.image.regularUrl : ltoFallbackImageUrl);
          const ltoMainImageMeta = chosenLtoMainImage != null
            ? chosenLtoMainImage.description
            : (country.defaultLto && country.defaultLto.image ? country.defaultLto.image.meta : "");

          // Use the chosen image, or fallback to the hero image
          const chosenLtoSecondImage = images.find(x => x.locationImageType === LocationImageType.LtoSecondary);
          const ltoSecondImageUrl = chosenLtoSecondImage != null ? chosenLtoSecondImage.smallUrl : ltoFallbackImageUrl;
          const ltoSecondImagemeta = chosenLtoSecondImage != null ? chosenLtoSecondImage.meta : ""

          return (
            <div className="country-overview">
              <div className="width-wrapper">
                <div className="columns is-marginless">
                  <div className="column is-8-tablet">
                    <div
                      className="blog-post country-overview__intro"
                      dangerouslySetInnerHTML={{ __html: country.introContent ? country.introContent.content.html : undefined }}
                    />

                    {MatchState(sectionContentState, {
                      data: sectionContent => {
                        if (sectionContent.length) {
                          return (
                            <a onClick={jumpToContentSections} className="secondary-button country-overview__jump-to-content">
                              <span>More about {country.name} <Icon icon={Icon.Icomoon.arrowDown2} /></span>
                            </a>
                          );
                        }
                      },
                    })}

                  </div>
                  <div className="column is-4-tablet">
                    <div className="country-overview__ltos">
                      <h3>Our trusted local experts in {country.name}</h3>
                      <ul>
                        <li>We handpick only the very best local travel companies</li>
                        <li>They live and work in your destination and know it better than any remotely-based agency</li>
                        <li>So you have the most up to date local knowledge at your disposal</li>
                      </ul>
                      <LinkButton buttonClass="secondary-button" to={WebRoutes.countryExperts(country.slug, country.parent.slug)}>
                        <span>
                          Meet our local experts in {country.name}
                        </span>
                      </LinkButton>

                      {(country.averageLtoRating != null && country.averageLtoRating > 0) &&
                        <div className="country-overview__ltos-rating">
                          <StarRating currentValue={country.averageLtoRating} numberOfStars={5} maximumValue={5} ></StarRating>
                          <span className="country-overview__ltos-rating-number">({country.averageLtoRating.toFixed(1)})</span>
                        </div>
                      }
                      <div className="country-overview__ltos-images-wrap">
                        <div className="country-overview__ltos-images">
                          <LazyLoadImage
                            className="country-overview__ltos-image country-overview__ltos-image--secondary"
                            src={ltoSecondImageUrl}
                            alt={ltoSecondImagemeta}
                          />
                          <LazyLoadImage
                            className="country-overview__ltos-image country-overview__ltos-image--primary"
                            src={ltoMainImageUrl}
                            alt={ltoMainImageMeta}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {MatchState(itinerariesContentState, {
                data: itineraries => itineraries && itineraries.length > 0 && (
                  <div className="country-overview__trips width-wrapper">
                    <div className="country-overview__trips__background">
                      <header>
                        <h2>Inspiring Itineraries</h2>
                        <div className="columns">
                          <p className="column is-10-tablet is-8-desktop">
                            Everyone likes a personal touch which is why our local experts craft your trip to be unique to you.{" "}
                            To inspire you they have put together some of their own suggestions for a truly memorable holiday.{" "}
                            Have a browse. You can book one of these trips as it is, or personalise to your heart's content.
                          </p>
                        </div>
                      </header>

                      <div className="country-overview__trips__ideas">
                        <CardContainer
                          collectionKey={CollectionKeyHelper.LocationItineraries(country.id)}
                          cardLimit={20}
                          renderCardWrapper={elements => (
                            <SwipeCarousel nextLabel="More ideas">
                              {elements}
                            </SwipeCarousel>
                          )}
                          filters={{
                            locations: [country.id],
                            types: [ContentType.SampleItinerary],
                          }}
                        />

                        <LinkButton
                          buttonClass="primary-button"
                          className="country-overview__trips__cta"
                          to={WebRoutes.countryTripIdeas(country.slug, country.parent.slug)}
                        >
                          View all trip ideas
                        </LinkButton>
                      </div>

                      <div className="clearfix" />
                      <div className="country-overview__trips__mask" />
                    </div>
                  </div>
                ),
              })}

              {MatchState(sectionContentState, {
                data: sectionContent => {
                  if (!sectionContent.length) {
                    // Don't render anything if there aren't any custom sections
                    return;
                  }

                  // #1531 Enable selection of content header image
                  const contentHeaderImage = images.find(x => x.locationImageType === LocationImageType.ContentHeader);
                  const contentHeaderImageUrl = contentHeaderImage
                    ? contentHeaderImage.largeUrl
                    : (country.heroImage ? country.heroImage.largeUrl : "/images/missing-image.jpg");

                  const contentHeaderImageDescription = contentHeaderImage
                    ? contentHeaderImage.meta
                    : (country.heroImage ? country.heroImage.meta : "");

                  const { width } = useWindowDimensions();
                  const isMobile = width < ResponsiveBreakpoints.tablet;
                  const navItems: NavMenuItem[] = getNavItems(sectionContent);

                  return (
                    <div className="country-overview__sections">
                      <LazyLoadImage
                        className="country-overview__sections-hero"
                        src={contentHeaderImageUrl}
                        alt={contentHeaderImageDescription}
                      />

                      <div className="width-wrapper">
                        <div className="columns country-overview__sections__columns">
                          <div className="country-overview__sections__secondary-nav-container column is-3-tablet is-paddingless is-marginless">
                            <Sticky
                              enabled={!isMobile}
                              innerZ={5}
                              top={100}
                              bottomBoundary=".country-overview__sections__location-content-container"
                            >
                              <PageScrollNav
                                label={isMobile ? "Jump to section..." : country.name}
                                items={navItems}
                                onItemClick={(id) => zenscroll.to(document.querySelector(`[data-scroll-id="${id}"]`))}
                                onSelectChange={(id) => zenscroll.toY(getDomItemYPositionWithStickyNavOffset(id))}
                              />
                              <CountrySidebarCta countrySlug={country.slug} />
                            </Sticky>
                          </div>
                          <div className="country-overview__sections__location-content-container column is-9-tablet is-paddingless">
                            {sectionContent.map(content => (
                              <LocationContentSection
                                key={content.id}
                                content={content}
                                region={country.parent.slug}
                              />
                            ))}
                            <CountrySidebarCta countrySlug={country.slug} />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                },
              })}
              <div className="country-overview__trust">
                <TrustSection country={country} />
              </div>
            </div>
          );
        },
      })}
    </>
  );
};

const storeToProps = (store: IWebStoreState): StoreProps => {
  const countryState = store.country;
  let sectionContentState: IState<IContentCollection<IContentLocationSection>>;
  let itinerariesContentState: IState<IContentCollection<IContentSampleItinerary>>;

  if (countryState.data) {
    const contentState = store.contentByCollection;

    const sectionCollection = contentState.data[CollectionKeyHelper.LocationOverviewSections(countryState.data.id)];
    sectionContentState = sectionCollection as IState<IContentCollection<IContentLocationSection>>;

    const itineraryCollection = contentState.data[CollectionKeyHelper.LocationItineraries(countryState.data.id)];
    itinerariesContentState = itineraryCollection as IState<IContentCollection<IContentSampleItinerary>>;
  }

  return {
    countryState,
    sectionContentState,
    itinerariesContentState,
  };
};

export const Overview = connect(storeToProps)(PageComponent);
